import type {
  ApiContract,
  BaseContentDto as ConnectorBaseContentDto,
  EPGConfigDto as ConnectorEPGConfigDto,
  EpisodeDetailDto as ConnectorEpisodeDetailDto,
  LinearContentDto as ConnectorLinearContentDto,
  MovieDetailDto as ConnectorMovieDetailDto,
  PageDto as ConnectorPageDto,
  SessionDto,
  SeriesDetailDto as ConnectorSeriesDetailDto,
  SubscriptionDto as ConnectorSubscriptionDto,
  SubscriptionOfferDto as ConnectorSubscriptionOfferDto,
} from "hyview-api-connector";
import {
  createApiLayer,
  createAuthLayer,
  createSubscriptionLayer,
} from "hyview-api-connector";
import { jwtDecode } from "jwt-decode";
import { Platform } from "react-native";

import theme from "$src/core/constants/theme";
import type { ApiServices } from "$src/core/contexts/api";
import type {
  AccountDto,
  BaseContentDto,
  ContentDto,
  EPGConfigDto,
  EpisodeContentDto,
  LinearContentDto,
  MovieContentDto,
  PageDto,
  ProfileDto,
  SeriesContentDto,
  SubscriptionDto,
  SubscriptionOfferDto,
  SubscriptionOffersDto,
} from "$src/core/models/dto";
import fetch from "$src/core/services/fetch";
import SecureStorage from "$src/core/services/secure-storage";
import getDeviceInfo from "$src/core/utils/device/get-device-info";
import getStableDeviceId from "$src/core/utils/device/get-stable-device-id";
import convertHtmlToMarkdown from "$src/core/utils/html/convert-html-to-markdown";
import {
  CLEENG_COUNTRY,
  CLEENG_CURRENCY,
  CLEENG_ENVIRONMENT,
  CLEENG_PUBLISHER_ID,
  HYVIEW_GRAPHQL_ENDPOINT,
} from "$src/flavors/hyview/constants/env";
import {
  addLocalProfile,
  deleteLocalProfile,
  getLocalProfiles,
  hasLocalProfileStore,
  updateLocalProfile,
} from "$src/flavors/hyview/services/local/local-profiles";
import addWatchlistItem from "$src/flavors/hyview/services/local/mutations/add-watchlist-item";
import removeWatchlistItem from "$src/flavors/hyview/services/local/mutations/remove-watchlist-item";
import getWatchlist from "$src/flavors/hyview/services/local/queries/get-watchlist";

type JwtCustomerPayload = {
  customerId?: string | number;
};

const SEED_TINT = theme.profile.tints[0];

const apiLayer = createApiLayer({
  vendor: "prepr",
  endpoint: HYVIEW_GRAPHQL_ENDPOINT,
  fetch,
});

const authLayer = createAuthLayer({
  vendor: "cleeng",
  publisherId: CLEENG_PUBLISHER_ID,
  environment: CLEENG_ENVIRONMENT,
  fetch,
  ...(CLEENG_ENVIRONMENT === "production" && {
    authEndpoint: "https://auth.api.prod.cleeng.com",
  }),
});

const getAccessToken = () => SecureStorage.getAsync("auth.accessToken");

const subscriptionLayer = createSubscriptionLayer({
  vendor: "cleeng",
  publisherId: CLEENG_PUBLISHER_ID,
  environment: CLEENG_ENVIRONMENT,
  getAccessToken,
  fetch,
});

const mapSession = (session: SessionDto) => ({
  accessToken: session.jwt,
  refreshToken: session.refreshToken,
  userId: session.customerId,
});

const getStoredAccessToken = async () => {
  const accessToken = await SecureStorage.getAsync("auth.accessToken");
  if (!accessToken) {
    throw new Error("No access token found");
  }
  return accessToken;
};

const getCurrentCustomerId = async () => {
  const storedCustomerId = await SecureStorage.getAsync("auth.userId");
  if (storedCustomerId) {
    return storedCustomerId;
  }

  const accessToken = await getStoredAccessToken();
  const decodedAccessToken = jwtDecode<JwtCustomerPayload>(accessToken);
  const { customerId } = decodedAccessToken;
  if (!customerId) {
    throw new Error("No customer id found");
  }

  return customerId.toString();
};

const unixSecondsToIso = (timestamp: number | null) => {
  if (!timestamp) {
    return "";
  }

  return new Date(timestamp * 1000).toISOString();
};

const mapSubscriptionOffer = (
  offer: ConnectorSubscriptionOfferDto,
): SubscriptionOfferDto => ({
  id: offer.longId,
  title: offer.title,
  description: offer.description,
  price: offer.price,
  period: offer.billingCycle.periodUnit === "year" ? "year" : "month",
});

const mapSubscription = (
  subscription: ConnectorSubscriptionDto,
): SubscriptionDto => ({
  id: subscription.offerId,
  offerId: subscription.offerId,
  status: subscription.status === "active" ? "active" : "inactive",
  startDate: "",
  expireDate: unixSecondsToIso(subscription.expiresAt),
});

const handleLogin = async (email: string, password: string) =>
  mapSession(await authLayer.login(email, password));

const CLEENG_MEDIA_STORE_BASE =
  CLEENG_ENVIRONMENT === "sandbox"
    ? "https://mediastoreapi-sandbox.cleeng.com"
    : "https://mediastoreapi.cleeng.com";

type CleengSessionResponse = {
  responseData?: { jwt: string; refreshToken: string; customerId: string };
};

const handleRegister = async (
  email: string,
  password: string,
  details?: { firstName?: string; lastName?: string },
) => {
  // ponytail: local register until hyview-api-connector sends locale/country/currency
  const httpFetch = fetch ?? globalThis.fetch;
  const response = await httpFetch(`${CLEENG_MEDIA_STORE_BASE}/customers`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      email,
      password,
      ...(details?.firstName?.trim() && {
        firstName: details.firstName.trim(),
      }),
      ...(details?.lastName?.trim() && { lastName: details.lastName.trim() }),
      publisherId: String(CLEENG_PUBLISHER_ID),
      locale: "en_US",
      country: CLEENG_COUNTRY,
      currency: CLEENG_CURRENCY,
    }),
  });
  if (!response.ok) {
    const text = await response.text().catch(() => response.statusText);
    throw new Error(
      `Cleeng POST /customers failed (${response.status}): ${text}`,
    );
  }

  const payload = (await response.json()) as CleengSessionResponse;
  const data = payload.responseData;
  if (!data?.jwt) {
    throw new Error("Cleeng register: no JWT in response");
  }

  return mapSession({
    jwt: data.jwt,
    refreshToken: data.refreshToken,
    customerId: data.customerId,
  });
};

const handleRefresh = async (refreshToken: string) =>
  mapSession(await authLayer.refreshToken(refreshToken));

const handleGeneratePairingCode = async () =>
  authLayer.generatePairingCode(
    getStableDeviceId(),
    getDeviceInfo().device_name,
  );

const handleAuthorizePairingCode = async (code: string) => {
  await authLayer.authorizePairingCode(code, await getStoredAccessToken());
  return true;
};

const handleCheckPairingCode = async (code: string) => {
  const session = await authLayer.checkPairingCode(getStableDeviceId(), code);
  if (!session) {
    throw new Error("Pairing code has not been authorized yet");
  }
  return mapSession(session);
};

const handleGetAccount = async (): Promise<AccountDto> => {
  const customer = await authLayer.getCustomer(
    await getCurrentCustomerId(),
    await getStoredAccessToken(),
  );

  return {
    id: customer.id,
    email: customer.email,
    firstName: customer.firstName ?? "",
    lastName: customer.lastName ?? "",
  };
};

const handleGetProfiles = async (): Promise<ProfileDto[]> => {
  // No profile backend: the first read seeds the main profile from the signed-in customer, and that one cannot be removed.
  if (!hasLocalProfileStore()) {
    const account = await handleGetAccount();

    // Keyed on the account id: installs from before profiles existed hold their watchlist there.
    await addLocalProfile(account.firstName || account.email, SEED_TINT, {
      id: account.id,
      isMain: true,
    });
  }

  return getLocalProfiles();
};

const handleGetSubscriptionOffers =
  async (): Promise<SubscriptionOffersDto> => {
    const offers = await subscriptionLayer.getOffers();

    return offers.filter((offer) => offer.active).map(mapSubscriptionOffer);
  };

const handleGetSubscription = async (): Promise<SubscriptionDto | null> => {
  const subscription = await subscriptionLayer.getSubscription(
    await getCurrentCustomerId(),
  );

  return subscription ? mapSubscription(subscription) : null;
};

const handleActivateSubscriptionOffer = async (
  offerId: string,
  coupon?: string,
) => {
  await subscriptionLayer.activateOffer({
    offerId,
    customerId: await getCurrentCustomerId(),
    couponCode: coupon,
  });
};

const handleSwitchSubscriptionOffer = async (
  switchDirection: "upgrade" | "downgrade",
  offerId: string,
  toOfferId: string,
  coupon?: string,
) => {
  await subscriptionLayer.switchOffer({
    fromOfferId: offerId,
    toOfferId,
    customerId: await getCurrentCustomerId(),
    switchDirection,
    couponCode: coupon,
  });
};

const handleCancelSubscription = async (offerId: string) => {
  await subscriptionLayer.cancelSubscription(
    offerId,
    await getCurrentCustomerId(),
  );
};

const handleGetMenu = async () => {
  // TODO: pass Platform.isTV to getMenu function and check the configuration on prepr/hyview-connector, the menu items are missing icons and `guide` item
  const menu = await apiLayer.getMenu();
  if (Platform.isTV) {
    if (menu.navBar.every((item) => item.type !== "profile")) {
      const profile = menu.tabBar.find((item) => item.type === "profile");
      if (profile) {
        menu.navBar.push(profile);
      }
    }
    menu.tabBar = menu.navBar;
  }
  return menu;
};

const getSearchItems = async ({
  query = "",
  tags,
  limit,
}: { query?: string; tags?: string[]; limit?: number } = {}) => {
  const { movies, series } = await apiLayer.getSearchResults(query, tags);
  const items = [...movies, ...series].map(mapContent);

  return typeof limit === "number" ? items.slice(0, limit) : items;
};

const handleGetSearchRecommendations = async () => ({
  items: await getSearchItems({ limit: 10 }),
});

const handleGetWatchlist = (api: ApiContract, profileId: string) =>
  getWatchlist(api, profileId);

const handleGetAbout = async () => {
  const about = await apiLayer.getAbout();
  return {
    ...about,
    items: about.items.map((item) => ({
      ...item,
      content: convertHtmlToMarkdown(item.content),
    })),
  };
};

const withSlug = <T extends { id: string; slug?: string }>(item: T) => ({
  ...item,
  slug: item.slug ?? item.id,
});

type ConnectorContent =
  | ConnectorBaseContentDto
  | NonNullable<ConnectorPageDto["hero"]>
  | NonNullable<ConnectorPageDto["sections"]>[number]["items"][number];

const mapBaseContent = (content: ConnectorContent): BaseContentDto => ({
  id: content.id,
  slug: "slug" in content && content.slug ? content.slug : content.id,
  title: content.title,
  subtitle: content.subtitle,
  shortDescription:
    "description" in content
      ? content.description
      : "shortDescription" in content
        ? content.shortDescription
        : undefined,
  longDescription:
    "longDescription" in content ? content.longDescription : undefined,
  image: content.image,
  images: content.images,
  tags: "tags" in content ? content.tags : undefined,
  publishDate: "publishDate" in content ? content.publishDate : undefined,
});

function mapContent(content: ConnectorContent): ContentDto {
  const base = mapBaseContent(content);

  switch (content.type) {
    case "movies":
      return {
        ...base,
        type: "movies",
        duration: "duration" in content ? content.duration : undefined,
        progress: "progress" in content ? content.progress : undefined,
        trailer: "trailer" in content ? content.trailer : undefined,
        playback: "playback" in content ? content.playback : undefined,
      };
    case "series":
      return { ...base, type: "series" };
    case "episodes":
      return {
        ...base,
        type: "episodes",
        duration: "duration" in content ? content.duration : undefined,
        progress: "progress" in content ? content.progress : undefined,
        playback: "playback" in content ? content.playback : undefined,
      };
    default:
      throw new Error("Unsupported content type");
  }
}

const mapOptionalContent = (content: ConnectorPageDto["hero"]) =>
  content ? mapContent(content) : undefined;

const mapPage = (page: ConnectorPageDto): PageDto => ({
  ...page,
  hero: mapOptionalContent(page.hero),
  sections: page.sections.map((section) => ({
    ...section,
    items: section.items.map(mapContent),
  })),
});

const mapMovieContent = (content: ConnectorMovieDetailDto): MovieContentDto =>
  withSlug(content);

const mapEpisodeContent = ({
  trailer: _trailer,
  ...content
}: ConnectorEpisodeDetailDto): EpisodeContentDto => withSlug(content);

const mapSeriesContent = ({
  playback: _playback,
  duration: _duration,
  progress: _progress,
  ...content
}: ConnectorSeriesDetailDto): SeriesContentDto =>
  withSlug({
    ...content,
    seasons: content.seasons?.map((season) => ({
      ...withSlug(season),
      episodes: season.episodes?.map(mapEpisodeContent),
    })),
  });

const mapLinearContent = (
  content: ConnectorLinearContentDto,
): LinearContentDto => withSlug(content);

const mapEPGConfig = (config: ConnectorEPGConfigDto): EPGConfigDto => ({
  daysBefore: config.daysBefore,
  daysAfter: config.daysAfter,
  primeTime: config.primetime,
  timezone: config.timezone,
});

const hyviewApi: ApiServices = {
  getTranslations: () => apiLayer.getTranslations(),
  getTheme: () => apiLayer.getTheme(),
  getMenu: handleGetMenu,
  getAbout: handleGetAbout,

  getPage: async (slug) => mapPage(await apiLayer.getStorefront(slug)),
  getStorefront: async () => mapPage(await apiLayer.getStorefront("home")),

  getMovieContent: async (id) =>
    mapMovieContent(await apiLayer.getMovieDetail(id)),
  getSeriesContent: async (id) =>
    mapSeriesContent(await apiLayer.getSeriesDetail(id)),
  getEpisodeContent: async (id) =>
    mapEpisodeContent(await apiLayer.getEpisodeDetail(id)),
  getLinearContent: async (id) =>
    mapLinearContent(await apiLayer.getLinearContent(id)),

  // The connector already returns this shape and preserves the CMS channel order.
  getEPG: (options) => apiLayer.getEPG(options),
  getEPGConfig: async () => mapEPGConfig(await apiLayer.getEPGConfig()),

  getSearchResults: async ({ query, tags } = {}) => ({
    items: await getSearchItems({ query, tags }),
  }),
  getSearchRecommendations: handleGetSearchRecommendations,

  login: handleLogin,
  register: handleRegister,
  refresh: handleRefresh,
  generatePairingCode: handleGeneratePairingCode,
  authorizePairingCode: handleAuthorizePairingCode,
  checkPairingCode: handleCheckPairingCode,

  getAccount: handleGetAccount,

  getSubscriptionOffers: handleGetSubscriptionOffers,
  getSubscription: handleGetSubscription,
  activateSubscriptionOffer: handleActivateSubscriptionOffer,
  switchSubscriptionOffer: handleSwitchSubscriptionOffer,
  cancelSubscription: handleCancelSubscription,

  getProfiles: handleGetProfiles,
  createProfile: addLocalProfile,
  updateProfile: updateLocalProfile,
  deleteProfile: deleteLocalProfile,
  getWatchlist: (profileId) => handleGetWatchlist(apiLayer, profileId),
  addWatchlistItem,
  removeWatchlistItem,
};

export default hyviewApi;
